import { Box } from "@mui/material"
import { FixedNumber } from "ethers"
import { parseUnits } from "ethers/lib/utils"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import Button from "src/components/Button"
import { enqueueToast } from "src/components/Toastify"
import TransactionSummary from "src/components/TransactionSummary"
import { Token, XFER } from "src/constants"
import { useActiveWeb3React } from "src/hooks"
import { fetchFerroBarUserDataAsync } from "src/state/xFer"
import { useUserFerroBar } from "src/state/xFer/hooks"
import {
  formatNumber,
  sanitizeStringForFN,
} from "src/utils/parseStringToBigNumber"
import useFerroBarHandlers from "../../hooks/useFerroBarHandlers"

interface Props {
  inputValue: string
  rate: FixedNumber
  stakingToken: Token
  earningToken: Token
  remark: string
  onDismiss: () => void
  onConfirm: () => void
  isUnstaking: boolean
  apr: number
}

const ConfirmStep = ({
  inputValue,
  rate,
  remark,
  onDismiss,
  onConfirm,
  stakingToken,
  earningToken,
  isUnstaking,
  apr,
}: Props) => {
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()

  const { onApprove } = useFerroBarHandlers()
  const dispatch = useDispatch()

  const converted = isUnstaking
    ? FixedNumber.from(sanitizeStringForFN(inputValue)).mulUnsafe(rate)
    : FixedNumber.from(sanitizeStringForFN(inputValue)).divUnsafe(rate)

  const { barData } = useUserFerroBar()
  const { allowance } = barData

  const handleApprove = useCallback(async () => {
    try {
      await onApprove()
      if (account) {
        dispatch(fetchFerroBarUserDataAsync(account))
      }
    } catch (e) {
      console.error(e)
      enqueueToast("error", t("FER is not approved"), t("Transaction Failed"))
    }
  }, [account, dispatch, onApprove, t])

  const isApproveSpending =
    allowance &&
    allowance.gt("0") &&
    allowance.lt(parseUnits(inputValue || "0", XFER.decimals))

  return (
    <>
      <Box maxWidth="400px" mb={2}>
        {remark}
      </Box>
      <TransactionSummary
        data={[
          {
            label: t(isUnstaking ? "Unstake" : "Stake"),
            amount: formatNumber(inputValue, 2, 18),
            token: stakingToken,
          },
          {
            label: t("Receive"),
            amount: formatNumber(converted, 2, 18),
            token: earningToken,
          },
        ]}
      />
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Box>{t("APR")}</Box>
        <Box>{formatNumber(apr, 2, 2)}%</Box>
      </Box>
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Box>{t("Rates")}</Box>
        <Box>{`1 xFER = ${formatNumber(rate, 2, 10)} FER`}</Box>
      </Box>
      <Box display="flex" mt={3}>
        <Button kind="outline" onClick={onDismiss} fullWidth>
          {t("Cancel")}
        </Button>
        <Button
          fullWidth
          onClick={isApproveSpending ? handleApprove : onConfirm}
          ml={1}
        >
          {isApproveSpending ? t("Approve") : t("Confirm")}
        </Button>
      </Box>
    </>
  )
}

export default ConfirmStep
